import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { bookingService } from "@/lib/services";
import { PageShell } from "@/components/nav";
import { useAuth } from "@/lib/auth-context";
import { ensureRoom, markRoomRead, sendMessage, subscribeToMessages } from "@/lib/firebaseChat";
import { isFirebaseConfigured } from "@/lib/firebase-config";
import type { ChatMessage } from "@/types/api";
import { extractErrorMessage } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/chats/$bookingId")({
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData({
      queryKey: ["booking", params.bookingId],
      queryFn: () => bookingService.get(params.bookingId),
    }),
  component: ChatRoom,
});

function ChatRoom() {
  const { bookingId } = Route.useParams();
  const { user } = useAuth();

  const { data: bookingResp } = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: () => bookingService.get(bookingId),
  });
  const booking = bookingResp?.data;

  /*
    Which side of *this* booking you are on, not what your account is.

    A host is a traveller too: booking a stay elsewhere makes them the guest in
    that conversation. Reading the account role instead put them on the host
    side of their own trip, so their messages were filed against the wrong
    unread counter and labelled as coming from the host.
  */
  const role: "guest" | "host" = booking && booking.guest_id === user?.id ? "guest" : "host";

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [unsupported, setUnsupported] = useState(!isFirebaseConfigured());
  const scrollRef = useRef<HTMLDivElement>(null);

  const host = booking?.property?.host;

  // Initialise room + subscribe
  useEffect(() => {
    if (!user || !booking || !isFirebaseConfigured()) return;

    /*
      Without a real host id there is no room worth opening. This used to fall
      back to the literal string "unknown-host", which wrote a room the host
      could never find — their inbox matches on their own id — so the guest
      would send messages into a conversation that, for the host, did not
      exist. Failing visibly is better than that.
    */
    const hostId = booking.property?.host?.id;
    if (!hostId) {
      setUnsupported(true);

      return;
    }

    let unsub: (() => void) | null = null;
    (async () => {
      try {
        await ensureRoom(bookingId, booking.guest_id, hostId, booking.property_id);
        unsub = subscribeToMessages(bookingId, setMessages, () => setUnsupported(true));
        await markRoomRead(bookingId, role);
      } catch (err) {
        console.warn("[chat] failed to init room", err);
        setUnsupported(true);
      }
    })();
    return () => {
      if (unsub) unsub();
    };
  }, [bookingId, booking, user, role]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const onSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !text.trim()) return;
    setSending(true);
    try {
      await sendMessage(bookingId, user.id, role, text);
      setText("");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Couldn't send message"));
    } finally {
      setSending(false);
    }
  };

  return (
    <PageShell>
      <div className="mx-auto flex h-[calc(100vh-4rem)] max-w-3xl flex-col px-4 py-4">
        <div className="rounded-2xl border border-border bg-card p-4">
          <Link to="/chats" className="text-xs font-semibold text-ink-muted hover:text-ink">
            ← All conversations
          </Link>
          <h1 className="mt-1 text-lg font-extrabold text-ink">
            {booking?.property?.name ?? "Booking chat"}
          </h1>
          <p className="text-xs text-ink-muted">
            {/* Whoever is on the other side of this booking — a host who has
                booked elsewhere is talking to their host, not to themselves. */}
            Booking {bookingId.slice(-6)} ·{" "}
            {role === "guest"
              ? (host?.name ?? "Host")
              : (booking?.guest?.name ?? booking?.guest_name ?? "Guest")}
          </p>
        </div>

        {unsupported ? (
          <div className="mt-6 rounded-2xl border border-accent/40 bg-accent/20 p-6 text-sm text-ink">
            Realtime chat is not available
          </div>
        ) : (
          <>
            <div
              ref={scrollRef}
              className="mt-3 flex-1 space-y-2 overflow-y-auto rounded-2xl border border-border bg-background p-4"
            >
              {messages.length === 0 && (
                <p className="py-10 text-center text-sm text-ink-subtle">
                  No messages yet — say hi 👋
                </p>
              )}
              {messages.map((m) => {
                const mine = m.senderId === user?.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
                        mine ? "bg-primary text-primary-foreground" : "bg-muted text-ink"
                      }`}
                    >
                      <p className="whitespace-pre-line break-words">{m.text}</p>
                      <p
                        className={`mt-1 text-[10px] ${mine ? "text-primary-foreground/80" : "text-ink-subtle"}`}
                      >
                        {formatDateTime(m.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <form
              onSubmit={onSend}
              className="mt-3 flex items-center gap-2 rounded-2xl border border-border bg-card p-2"
            >
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message…"
                className="flex-1 bg-transparent px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:outline-none"
              />
              <button
                type="submit"
                disabled={sending || !text.trim()}
                className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </>
        )}
      </div>
    </PageShell>
  );
}
